import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	inject,
	Input,
	NgZone,
	OnInit,
	Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { pairwise, startWith } from 'rxjs';
import {
	DividerComponent,
	FormGroupComponent,
	InfoGroupComponent,
	InputQuantityComponent,
	ItemComponent,
	ITab,
	LinkComponent,
	SelectComponent,
	SwitchComponent,
	TabsComponent,
	TagImageComponent,
	TileComponent,
	UploadComponent,
} from '@tecalisdev/infinity';
import { ICssConfigBody } from '../../../../../shared/models/master.models';
import { MasterService } from '../../../../../shared/services/master.service';
import { DesignThemeSection } from '../../models/design.models';
import { DesignHelperService } from '../../services/design-helper.service';
import { DesignThemePreviewComponent } from '../design-theme-preview/design-theme-preview.component';

type BrandKey = (typeof DesignHelperService.BRAND_KEYS)[number];

@Component({
	selector: 'app-design-theme-detail',
	imports: [
		NgTemplateOutlet,
		TranslatePipe,
		ReactiveFormsModule,
		DesignThemePreviewComponent,
		TileComponent,
		FormGroupComponent,
		SelectComponent,
		DividerComponent,
		InputQuantityComponent,
		ItemComponent,
		InfoGroupComponent,
		SwitchComponent,
		UploadComponent,
		TagImageComponent,
		LinkComponent,
		TabsComponent,
	],
	templateUrl: './design-theme-detail.component.html',
	styleUrl: './design-theme-detail.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DesignThemeDetailComponent),
			multi: true,
		},
	],
})
export class DesignThemeDetailComponent implements ControlValueAccessor, OnInit {
	@Input() themeMode?: string;
	/** Emite tras un cambio de usuario en el editor (no durante writeValue/hidratación). */
	@Output() themeEdited = new EventEmitter<void>();

	@HostBinding('attr.data-skin')
	get hostSkin(): string {
		return this.resolveSkin();
	}

	private masterService = inject(MasterService);
	private destroyRef = inject(DestroyRef);
	private ngZone = inject(NgZone);
	private cdr = inject(ChangeDetectorRef);
	public designHelper = inject(DesignHelperService);

	public form?: FormGroup;
	/** Snapshot estable para el preview (evitar getRawValue() en cada CD → reinicio Lottie). */
	public previewThemeData: Record<string, unknown> = {};
	public onChange: any = () => {};
	public onTouched: any = () => {};
	public acceptedFileTypes = ['.png', '.svg'];
	public customSpinnerAcceptedFileTypes = ['.svg', '.png', '.gif'];
	public base64Logo?: string;
	public base64Favicon?: string;
	public customSpinnerPreviewSrc?: string;
	public cssConfigs: ICssConfigBody[] = [];
	public sectionTabs: ITab[] = [];
	public selectedSection: DesignThemeSection = DesignThemeSection.GENERAL;
	readonly DesignThemeSection = DesignThemeSection;

	private _value: any;
	/** Evita marcar dirty en el padre mientras writeValue hidrata el form. */
	private suppressThemeEdited = false;
	/** Eliminar logo/favicon: solo entonces el FormData manda campo vacío (borrar en API). */
	private logoCleared = false;
	private faviconCleared = false;

	get value(): any {
		if (!this.form) return this._value;
		return this.form.value;
	}

	set value(val: any) {
		this._value = val;
		if (this.form && val) {
			this.form.patchValue(val, { emitEvent: false });
			this.syncPreviewThemeData();
		}
		if (this.form) this.onChange(this.transformFormValue(this.form.value));
		else this.onChange(this._value);

		this.onTouched();
	}

	ngOnInit(): void {
		const skin = this.resolveSkin();
		this.form = this.designHelper.createThemeForm(skin);
		this.previewThemeData = this.form.getRawValue();
		this.sectionTabs = this.designHelper.themeSectionTabs;

		this.masterService
			.getCssConfigs()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((configs) => {
				this.cssConfigs = configs.data;
				if (this._value && this._value.css_configs) {
					this.writeValue(this._value);
				}
			});

		this.bindFileControl('logo', (src) => (this.base64Logo = src));
		this.bindFileControl('favicon', (src) => (this.base64Favicon = src));

		this.form.get('custom_spinner')?.valueChanges.subscribe((value: FileList | File | string | null) => {
			if (!value || value instanceof File || typeof value === 'string') {
				if (value instanceof File) {
					this.updateCustomSpinnerPreview(value);
				} else if (typeof value === 'string') {
					this.customSpinnerPreviewSrc = value;
				} else if (!value) {
					this.customSpinnerPreviewSrc = undefined;
				}
				return;
			}
			if (value instanceof FileList && value.length > 0) {
				const file = value.item(0);
				if (file) {
					this.form?.patchValue({ custom_spinner: file }, { emitEvent: true });
				}
			}
		});

		for (const brandKey of DesignHelperService.BRAND_KEYS) {
			if (this.resolveSkin() === 'dark' && brandKey === 'brand_emphasis') {
				continue;
			}
			this.bindBrandCascade(brandKey);
		}

		this.form.valueChanges.subscribe((value) => {
			// Color-picker Infinity a veces emite fuera de NgZone → sin CD el preview
			// no corre ngOnChanges/updateStyles hasta otro click (p. ej. abrir el select).
			this.runInAngularZone(() => {
				this.previewThemeData = this.form?.getRawValue() ?? value;
				this.onChange(
					this.transformFormValue({
						...value,
						logo: this.resolveAssetForEmit('logo', value?.['logo']),
						favicon: this.resolveAssetForEmit('favicon', value?.['favicon']),
					}),
				);
				this.onTouched();
				if (!this.suppressThemeEdited) {
					this.themeEdited.emit();
				}
			});
		});
	}

	/**
	 * Evita borrar favicon/logo en la petición si el preview sigue visible
	 * (control null por desync) y el usuario no pulsó Eliminar.
	 */
	private resolveAssetForEmit(field: 'logo' | 'favicon', value: unknown): unknown {
		const asFile = this.designHelper.serializeThemeAsset(value);
		if (asFile instanceof File) {
			if (field === 'logo') this.logoCleared = false;
			else this.faviconCleared = false;
			return asFile;
		}
		if (typeof asFile === 'string' && asFile) {
			return asFile;
		}
		const cleared = field === 'logo' ? this.logoCleared : this.faviconCleared;
		if (cleared) {
			return null;
		}
		const preview = field === 'logo' ? this.base64Logo : this.base64Favicon;
		if (typeof preview === 'string' && preview) {
			return preview;
		}
		return asFile;
	}

	/**
	 * Garantiza CD del preview cuando el emisor (p. ej. tec-select color-picker) corre fuera de zona.
	 */
	private runInAngularZone(fn: () => void): void {
		if (NgZone.isInAngularZone()) {
			fn();
			this.cdr.markForCheck();
			return;
		}
		this.ngZone.run(() => {
			fn();
			this.cdr.markForCheck();
		});
	}

	private syncPreviewThemeData(): void {
		if (this.form) {
			this.previewThemeData = this.form.getRawValue();
		}
	}

	public sectionSelected(tab: { id: string }): void {
		this.selectedSection = tab.id as DesignThemeSection;
		this.sectionTabs = this.sectionTabs.map((t) => ({ ...t, selected: t.id === tab.id }));
	}

	public getColorHex(controlName: string): string {
		const raw = this.form?.get(controlName)?.value;
		const resolved = this.designHelper.resolveFormControlValue(raw);
		return typeof resolved === 'string' ? resolved : '';
	}

	public onHexInput(controlName: string, event: Event): void {
		const input = event.target as HTMLInputElement;
		const hex = (input.value || '').trim();
		if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(hex)) {
			return;
		}
		this.form?.get(controlName)?.setValue(this.designHelper.toColorPickerValue(hex), { emitEvent: true });
	}

	public resetBrandColors(): void {
		const skin = this.resolveSkin();
		this.form?.patchValue(this.designHelper.buildBrandResetPatch(skin), { emitEvent: true });
	}

	public resetTextColors(): void {
		const skin = this.resolveSkin();
		const text = this.designHelper.getDefaultTextColors(skin);
		this.form?.patchValue(
			{
				text_color_title: this.designHelper.toColorPickerValue(text.text_color_title),
				text_color_primary: this.designHelper.toColorPickerValue(text.text_color_primary),
				text_color_secondary: this.designHelper.toColorPickerValue(text.text_color_secondary),
				text_color_tertiary: this.designHelper.toColorPickerValue(text.text_color_tertiary),
				text_color: this.designHelper.toColorPickerValue(text.text_color),
			},
			{ emitEvent: true },
		);
	}

	public resetTextTypography(): void {
		this.form?.patchValue(
			{ text_typography: this.designHelper.typographyOptions[0]?.id ?? 'proxima-nova, sans-serif' },
			{ emitEvent: true },
		);
	}

	public resetButtonRadius(): void {
		this.form?.patchValue(
			{
				button_border_round: DesignHelperService.DEFAULT_BORDER_ROUND,
				button_icon_border_round: DesignHelperService.DEFAULT_BORDER_ROUND,
			},
			{ emitEvent: true },
		);
	}

	public resetButtonPrimaryColors(): void {
		const colors = this.designHelper.getDefaultButtonPrimaryColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetButtonSecondaryColors(): void {
		const colors = this.designHelper.getDefaultButtonSecondaryColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetButtonIconColors(): void {
		const colors = this.designHelper.getDefaultButtonIconColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetLinkStyle(): void {
		const defaults = this.designHelper.getDefaultLinkStyle(this.resolveSkin());
		this.form?.patchValue(
			{
				link_color: this.designHelper.toColorPickerValue(defaults.link_color),
				link_color_hover: this.designHelper.toColorPickerValue(defaults.link_color_hover),
				links_underline: defaults.links_underline,
				links_underline_hover: defaults.links_underline_hover,
			},
			{ emitEvent: true },
		);
	}

	public resetGraphicsColors(): void {
		const colors = this.designHelper.getDefaultGraphicsColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetFeedbackInfoColors(): void {
		this.patchFeedbackColors(this.designHelper.getDefaultFeedbackInfoColors(this.resolveSkin()));
	}

	public resetFeedbackAlertColors(): void {
		this.patchFeedbackColors(this.designHelper.getDefaultFeedbackAlertColors(this.resolveSkin()));
	}

	public resetFeedbackSuccessColors(): void {
		this.patchFeedbackColors(this.designHelper.getDefaultFeedbackSuccessColors(this.resolveSkin()));
	}

	public resetFeedbackErrorColors(): void {
		this.patchFeedbackColors(this.designHelper.getDefaultFeedbackErrorColors(this.resolveSkin()));
	}

	public resetLoadingProgress(): void {
		const defaults = this.designHelper.getDefaultLoadingProgress(this.resolveSkin());
		this.form?.patchValue(
			{
				progress_fill_color: this.designHelper.toColorPickerValue(defaults.progress_fill_color),
				progress_border_round: defaults.progress_border_round,
			},
			{ emitEvent: true },
		);
	}

	public resetCaptureGuideColor(): void {
		const hex = this.designHelper.getDefaultCaptureGuideColor(this.resolveSkin());
		this.form?.patchValue({ capture_guide_color: this.designHelper.toColorPickerValue(hex) }, { emitEvent: true });
	}

	public resetFieldInputColors(): void {
		const colors = this.designHelper.getDefaultFieldInputColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetFieldToggleColors(): void {
		const colors = this.designHelper.getDefaultFieldToggleColors(this.resolveSkin());
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public resetFieldInputRadius(): void {
		this.form?.patchValue(
			{ input_border_round: DesignHelperService.DEFAULT_INPUT_BORDER_ROUND },
			{ emitEvent: true },
		);
	}

	public resetAdvancedRadius(): void {
		const defaults = this.designHelper.getDefaultAdvancedRadius();
		this.form?.patchValue(defaults, { emitEvent: true });
	}

	public resetAdvancedBranding(): void {
		this.form?.patchValue(
			{
				watermark: false,
				show_product_name: false,
			},
			{ emitEvent: true },
		);
	}

	private patchFeedbackColors(colors: Record<string, string>): void {
		const patch: Record<string, unknown> = {};
		for (const [key, hex] of Object.entries(colors)) {
			patch[key] = this.designHelper.toColorPickerValue(hex);
		}
		this.form?.patchValue(patch, { emitEvent: true });
	}

	public writeValue(value: any): void {
		this._value = value;
		this.suppressThemeEdited = true;
		this.logoCleared = false;
		this.faviconCleared = false;
		try {
			if (this.form && value) {
				// Conservar URL/string en el form (no convertir a File): evita carrera async
				// donde el preview queda visible pero el control/padre tienen null → borrado fantasma.
				if (value.logo) {
					if (value.logo instanceof File) {
						const reader = new FileReader();
						reader.onload = () => {
							this.base64Logo = reader.result as string;
						};
						reader.readAsDataURL(value.logo);
						this.form.patchValue({ logo: value.logo }, { emitEvent: false });
					} else if (typeof value.logo === 'string' && value.logo) {
						this.base64Logo = value.logo;
						this.form.patchValue({ logo: value.logo }, { emitEvent: false });
					}
				} else if (value.logo === null) {
					this.base64Logo = undefined;
					this.form.patchValue({ logo: null }, { emitEvent: false });
				}

				if (value.favicon) {
					if (value.favicon instanceof File) {
						const reader = new FileReader();
						reader.onload = () => {
							this.base64Favicon = reader.result as string;
						};
						reader.readAsDataURL(value.favicon);
						this.form.patchValue({ favicon: value.favicon }, { emitEvent: false });
					} else if (typeof value.favicon === 'string' && value.favicon) {
						this.base64Favicon = value.favicon;
						this.form.patchValue({ favicon: value.favicon }, { emitEvent: false });
					}
				} else if (value.favicon === null) {
					this.base64Favicon = undefined;
					this.form.patchValue({ favicon: null }, { emitEvent: false });
				}

				if (this.cssConfigs && this.cssConfigs?.length > 0) {
					const skin = this.resolveSkin();
					const patchData = this.designHelper.restoreThemeValue(value, this.cssConfigs, skin);
					const normalizedValue: any = {};
					Object.keys(patchData).forEach((key) => {
						normalizedValue[key] = patchData[key];
					});
					this.form.patchValue(normalizedValue, { emitEvent: false });
					const alignPatch = this.designHelper.buildBrandAlignPatch(skin, this.form.getRawValue());
					if (Object.keys(alignPatch).length) {
						this.form.patchValue(alignPatch, { emitEvent: false });
					}
					if (skin === 'dark') {
						const feedbackPatch = this.designHelper.migrateStaleDarkFeedbackDefaults(
							this.form.getRawValue(),
						);
						if (Object.keys(feedbackPatch).length) {
							this.form.patchValue(feedbackPatch, { emitEvent: false });
						}
					}
					this.syncPreviewThemeData();
				}

				this.setInitialCustomSpinnerPreview(value.custom_spinner);
			}

			if (this.form) {
				try {
					const raw = this.form.value;
					this.onChange(
						this.transformFormValue({
							...raw,
							logo: this.resolveAssetForEmit('logo', raw?.['logo']),
							favicon: this.resolveAssetForEmit('favicon', raw?.['favicon']),
						}),
					);
				} catch {
					// Race: el padre puede haber eliminado light/dark antes de getCssConfigs
				}
			}
		} finally {
			// Liberar tras el tick: cascadas/async no deben marcar dirty de hidratación.
			queueMicrotask(() => {
				this.suppressThemeEdited = false;
			});
		}
	}

	private transformFormValue(value: any): any {
		return this.designHelper.transformThemeValue(value, this.cssConfigs);
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.form?.disable();
		} else {
			this.form?.enable();
		}
	}

	private resolveSkin(): 'light' | 'dark' {
		return this.themeMode?.toLowerCase() === 'dark' ? 'dark' : 'light';
	}

	/** Skin del tema (preview Feedback dark ≠ chrome Admin light). */
	get isDarkSkin(): boolean {
		return this.resolveSkin() === 'dark';
	}

	private bindFileControl(controlName: 'logo' | 'favicon', onPreview: (src: string) => void): void {
		this.form?.get(controlName)?.valueChanges.subscribe((value: FileList | File | string) => {
			if (!value) return;

			const file = this.designHelper.fileFromUploadValue(value);
			if (!file) {
				return;
			}

			if (controlName === 'logo') this.logoCleared = false;
			else this.faviconCleared = false;

			// tec-upload emite FileList: normalizar a File con emitEvent para que el CVA/padre
			// y el fingerprint de dirty vean el archivo.
			const isListLike =
				typeof value === 'object' &&
				value !== null &&
				!(value instanceof File) &&
				'length' in value &&
				typeof (value as FileList).item === 'function';
			if (isListLike) {
				this.form?.patchValue({ [controlName]: file }, { emitEvent: true });
				return;
			}

			const reader = new FileReader();
			reader.onload = () => onPreview(reader.result as string);
			reader.readAsDataURL(file);
		});
	}

	private bindBrandCascade(brandKey: BrandKey): void {
		const control = this.form?.get(brandKey);
		if (!control) {
			return;
		}
		control.valueChanges
			.pipe(startWith(control.value), pairwise(), takeUntilDestroyed(this.destroyRef))
			.subscribe(([prev, next]) => {
				const prevHex = this.designHelper.resolveFormControlValue(prev);
				const nextHex = this.designHelper.resolveFormControlValue(next);
				if (typeof prevHex !== 'string' || !prevHex || typeof nextHex !== 'string' || !nextHex) {
					return;
				}
				if (this.designHelper.areThemeColorsEqual(prevHex, nextHex)) {
					return;
				}
				const skin = this.resolveSkin();
				const targets = DesignHelperService.getBrandCascadeMap(skin)[brandKey];
				const currentValues: Record<string, unknown> = {
					...(this.form?.getRawValue() ?? {}),
				};
				for (const variable of targets) {
					currentValues[variable] = this.form?.get(variable)?.value;
				}
				const alsoMatchHexes =
					brandKey === 'brand_tone_tertiary'
						? this.designHelper.getToneTertiaryCascadeAlsoMatchHexes(skin, currentValues)
						: [];
				const patch = this.designHelper.buildBrandCascadePatch(brandKey, nextHex, {
					previousBrandHex: prevHex,
					currentValues,
					skin,
					alsoMatchHexes,
				});
				if (Object.keys(patch).length) {
					this.runInAngularZone(() => {
						this.form?.patchValue(patch, { emitEvent: true });
					});
				}
			});
	}

	private updateCustomSpinnerPreview(file: File): void {
		const reader = new FileReader();
		reader.onload = () => {
			this.customSpinnerPreviewSrc = reader.result as string;
		};
		reader.readAsDataURL(file);
	}

	private setInitialCustomSpinnerPreview(value: File | string | null | undefined): void {
		if (!value) {
			this.customSpinnerPreviewSrc = undefined;
			return;
		}

		if (value instanceof File) {
			this.updateCustomSpinnerPreview(value);
			return;
		}

		if (typeof value === 'string') {
			this.customSpinnerPreviewSrc = value;
		}
	}

	public changeCustomSpinner(): void {
		this.customSpinnerPreviewSrc = undefined;
		this.form?.patchValue({ custom_spinner: null }, { emitEvent: true });
	}

	public clearLogo(): void {
		this.logoCleared = true;
		this.base64Logo = undefined;
		this.form?.patchValue({ logo: null }, { emitEvent: true });
	}

	public clearFavicon(): void {
		this.faviconCleared = true;
		this.base64Favicon = undefined;
		this.form?.patchValue({ favicon: null }, { emitEvent: true });
	}
}
